// V3 Agenda — schermata prenotazioni esclusivamente su /api/v2.
// Prova Fase 5: nessuna dipendenza da plugin_* — la console non sa
// che il verticale è hair (o altro). Stesso codice vale per tutti.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });

export function V3AgendaPage() {
  const [bookings, setBookings] = useState<Row[]>([]);
  const [services, setServices] = useState<Row[]>([]);
  const [resources, setResources] = useState<Row[]>([]);
  const [people, setPeople] = useState<Row[]>([]);
  const [customers, setCustomers] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', service_id: '', person_id: '',
    resource_id: '', date: new Date().toISOString().slice(0, 10), start: '10:00', end: '11:00',
  });

  const load = useCallback(async () => {
    try {
      const [b, s, r, p, c] = await Promise.all([
        apiFetch('/api/v2/bookings'), apiFetch('/api/v2/services'),
        apiFetch('/api/v2/resources'), apiFetch('/api/v2/people'), apiFetch('/api/v2/customers'),
      ]);
      setBookings(await b.json()); setServices(await s.json());
      setResources(await r.json()); setPeople(await p.json());
      setCustomers(await c.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const nameOf = (list: Row[], id: unknown, field = 'name') =>
    list.find((x) => String(x.id) === String(id))?.[field] ?? '—';

  const byDay = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const b of bookings) {
      const first = b.segments?.[0];
      const day = first ? String(first.start_at).slice(0, 10) : 'senza data';
      const arr = map.get(day) || [];
      arr.push(b);
      map.set(day, arr);
    }
    return [...map.entries()].sort(([a], [d]) => d.localeCompare(a));
  }, [bookings]);

  const create = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/bookings', {
        method: 'POST',
        body: JSON.stringify({
          customer_name: form.customer_name || 'Cliente',
          customer_phone: form.customer_phone || null,
          service_id: form.service_id ? Number(form.service_id) : null,
          person_id: form.person_id ? Number(form.person_id) : null,
          resource_id: form.resource_id ? Number(form.resource_id) : null,
          start_at: `${form.date}T${form.start}:00Z`,
          end_at: `${form.date}T${form.end}:00Z`,
          source: 'backoffice',
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setForm({ ...form, customer_name: '', customer_phone: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: number, status: string) => {
    await apiFetch(`/api/v2/bookings/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    await load();
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={card}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuova prenotazione</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <input style={input} placeholder="Nome cliente" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <input style={input} placeholder="Telefono" value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
          <select style={input} value={form.service_id}
            onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
            <option value="">Servizio…</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.duration_min ? ` (${s.duration_min}′)` : ''}</option>)}
          </select>
          <select style={input} value={form.person_id}
            onChange={(e) => setForm({ ...form, person_id: e.target.value })}>
            <option value="">Operatore…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.display_name} ({p.role})</option>)}
          </select>
          <select style={input} value={form.resource_id}
            onChange={(e) => setForm({ ...form, resource_id: e.target.value })}>
            <option value="">Risorsa…</option>
            {resources.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.type_code}</option>)}
          </select>
          <input style={input} type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={input} type="time" value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })} />
            <input style={input} type="time" value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })} />
          </div>
          <button style={btn()} disabled={saving} onClick={() => void create()}>
            {saving ? 'Salvataggio…' : 'Crea prenotazione'}
          </button>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12, marginTop: 12 }}>
          API: /api/v2 · {services.length} servizi · {resources.length} risorse · {people.length} persone · {customers.length} clienti
        </p>
      </div>

      <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
        {byDay.length === 0 && <div style={card}><p className="sk-muted">Nessuna prenotazione.</p></div>}
        {byDay.map(([day, list]) => (
          <div key={day} style={card}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14, textTransform: 'capitalize' }}>{day}</h4>
            {list.map((b) => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)' }}>
                <div>
                  <strong>{b.customer_name || 'Cliente'}</strong>
                  {(b.segments || []).map((s: Row) => (
                    <span key={s.id} style={{ display: 'block', fontSize: 13, color: '#6b7280' }}>
                      {String(s.start_at).slice(11, 16)}–{String(s.end_at).slice(11, 16)} · {nameOf(services, s.service_id)} · {nameOf(people, s.person_id, 'display_name')} · {nameOf(resources, s.resource_id)}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{b.status}</span>
                  {b.status !== 'completed' && b.status !== 'cancelled' && (
                    <>
                      <button style={btn('#059669')} onClick={() => void setStatus(b.id, 'completed')}>Ok</button>
                      <button style={btn('#dc2626')} onClick={() => void setStatus(b.id, 'cancelled')}>✕</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
